/**
 * Shared layout wrapper for Sayz legal/compliance pages.
 *
 * Design: single column, generous reading rhythm, understated typography.
 * Public (no auth required) so prospective customers can review before signup.
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function LegalShell({ title, subtitle, lastUpdated, children, testId = "legal-page" }) {
    return (
        <div className="min-h-screen bg-background text-foreground" data-testid={testId}>
            <header className="border-b border-border/60">
                <div className="mx-auto max-w-3xl px-6 md:px-10 py-6 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="legal-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Giriş sayfasına dön</span>
                    </Link>
                    <span className="font-mono text-[11px] tracking-wider uppercase text-muted-foreground">
                        Sayz
                    </span>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-6 md:px-10 py-14 md:py-20">
                <div className="mb-10">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                        Yasal Bilgilendirme
                    </p>
                    <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3" data-testid="legal-title">
                        {title}
                    </h1>
                    {subtitle ? (
                        <p className="text-base text-muted-foreground max-w-2xl">{subtitle}</p>
                    ) : null}
                    {lastUpdated ? (
                        <p className="mt-4 text-xs text-muted-foreground">
                            Son güncelleme: <span className="font-medium text-foreground/80">{lastUpdated}</span>
                        </p>
                    ) : null}
                </div>

                <article className="prose-sayz" data-testid="legal-content">
                    {children}
                </article>

                <footer className="mt-16 pt-8 border-t border-border/60 flex flex-wrap gap-4 justify-between text-xs text-muted-foreground">
                    <div className="flex gap-5">
                        <Link to="/legal/kvkk" className="hover:text-foreground transition-colors" data-testid="legal-footer-kvkk">KVKK</Link>
                        <Link to="/legal/privacy" className="hover:text-foreground transition-colors" data-testid="legal-footer-privacy">Gizlilik Politikası</Link>
                        <Link to="/legal/terms" className="hover:text-foreground transition-colors" data-testid="legal-footer-terms">Kullanım Şartları</Link>
                        <Link to="/legal/cookies" className="hover:text-foreground transition-colors" data-testid="legal-footer-cookies">Çerez Politikası</Link>
                    </div>
                    <span>hello@atamis.global</span>
                </footer>
            </main>

            {/* Prose typography — scoped to legal pages only. */}
            <style>{`
                .prose-sayz { line-height: 1.75; font-size: 15px; color: hsl(var(--foreground) / 0.88); }
                .prose-sayz h2 { font-size: 20px; font-weight: 600; margin-top: 2.5rem; margin-bottom: 0.85rem; letter-spacing: -0.005em; }
                .prose-sayz h3 { font-size: 15px; font-weight: 600; margin-top: 1.8rem; margin-bottom: 0.6rem; text-transform: uppercase; letter-spacing: 0.04em; color: hsl(var(--foreground) / 0.7); }
                .prose-sayz p { margin-bottom: 1rem; }
                .prose-sayz ul { margin-bottom: 1.1rem; padding-left: 1.3rem; }
                .prose-sayz ul li { margin-bottom: 0.35rem; list-style-type: disc; }
                .prose-sayz ol { margin-bottom: 1.1rem; padding-left: 1.3rem; }
                .prose-sayz ol li { margin-bottom: 0.35rem; list-style-type: decimal; }
                .prose-sayz a { color: hsl(var(--primary)); text-decoration: underline; text-underline-offset: 2px; }
                .prose-sayz strong { color: hsl(var(--foreground)); font-weight: 600; }
                .prose-sayz .lead { font-size: 16px; color: hsl(var(--muted-foreground)); margin-bottom: 1.5rem; }
            `}</style>
        </div>
    );
}
